import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import {
    Box, Typography, Paper, Container,
    Alert, Grid, Chip, Button, Fade, Avatar,
    Tooltip, Zoom
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ClassIcon from '@mui/icons-material/Class';
import DownloadIcon from '@mui/icons-material/Download';
import HistoryIcon from '@mui/icons-material/History';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import styled, { keyframes } from 'styled-components';
import { formatDatePart, getMonthNameFromDate } from '../../../utils/customDate';

// Animations
const fadeIn = keyframes`
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
`;

const float = keyframes`
    0% { transform: translateY(0px); }
    50% { transform: translateY(-5px); }
    100% { transform: translateY(0px); }
`;

// Styled Components
const PageWrapper = styled(Box)`
    background-color: #f8fafc;
    min-height: 100vh;
    padding-bottom: 80px;
    animation: ${fadeIn} 0.5s ease-out;
`;

const PremiumHeader = styled(Box)`
    background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
    padding: 80px 0 100px;
    color: white;
    position: relative;
    overflow: hidden;
    
    &::before {
        content: '';
        position: absolute;
        top: -10%;
        left: -10%;
        width: 40%;
        height: 80%;
        background: radial-gradient(circle, rgba(99, 102, 241, 0.1) 0%, transparent 70%);
        border-radius: 50%;
    }
    
    &::after {
        content: '';
        position: absolute;
        bottom: -20%;
        right: -10%;
        width: 50%;
        height: 100%;
        background: radial-gradient(circle, rgba(99, 102, 241, 0.15) 0%, transparent 70%);
        border-radius: 50%;
    }
`;

const ContentContainer = styled(Container)`
    margin-top: -80px;
    position: relative;
    z-index: 2;
`;

const ClassSection = styled(Box)`
    margin-bottom: 50px;
`;

const SectionLabel = styled(Box)`
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 24px;
    padding: 0 8px;
    
    .line {
        flex-grow: 1;
        height: 2px;
        background: linear-gradient(90deg, #e2e8f0 0%, transparent 100%);
    }
`;

const RoutineCard = styled(Paper)`
    border-radius: 24px !important;
    padding: 24px;
    background: white !important;
    border: 1px solid #e2e8f0 !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
    height: 100%;
    display: flex;
    flex-direction: column;
    position: relative;
    overflow: hidden;
    
    &:hover {
        transform: translateY(-8px);
        box-shadow: 0 20px 40px -12px rgba(0, 0, 0, 0.1) !important;
        border-color: #6366f1 !important;
        
        .download-btn {
            background: #6366f1;
            color: white;
        }
        
        .icon-box {
            background: #eef2ff;
            color: #6366f1;
            transform: scale(1.1);
        }
    }
`;

const IconBox = styled(Box)`
    width: 48px;
    height: 48px;
    background: #f8fafc;
    color: #64748b;
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 20px;
    transition: all 0.3s ease;
`;

const TypeChip = styled(Chip)`
    position: absolute;
    top: 24px;
    right: 24px;
    font-weight: 700 !important;
    font-size: 0.7rem !important;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    height: 24px !important;
`;

const TeacherExamRoutine = () => {
    const { currentUser } = useSelector((state) => state.user);
    const [examRoutines, setExamRoutines] = useState([]);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState({ type: '', text: '' });

    useEffect(() => {
        if (currentUser?._id) {
            fetchExamRoutines();
        }
    }, [currentUser?._id]);

    const fetchExamRoutines = async () => {
        try {
            const result = await axios.get(`${process.env.REACT_APP_BASE_URL}/ExamRoutine/Teacher/${currentUser._id}`);
            if (Array.isArray(result.data)) {
                setExamRoutines(result.data);
            }
        } catch (error) {
            console.error('Error fetching exam routines:', error);
            setMessage({ type: 'error', text: 'Error loading exam routines' });
        }
        setLoading(false);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Not specified';
        const cleanDate = formatDatePart(dateString);
        return cleanDate;
    };

    const getExamTypeLabel = (type) => {
        const labels = {
            first: 'First Terminal',
            second: 'Second Terminal',
            mid: 'Mid Term',
            preboard: 'Pre-Board',
            final: 'Final'
        };
        return labels[type] || type;
    };

    const getExamTypeColors = (type) => {
        const configs = {
            first: { bg: '#eff6ff', text: '#1d4ed8', border: '#dbeafe' },
            second: { bg: '#f5f3ff', text: '#6d28d9', border: '#ede9fe' },
            mid: { bg: '#ecfdf5', text: '#047857', border: '#d1fae5' },
            preboard: { bg: '#fffbeb', text: '#b45309', border: '#fef3c7' },
            final: { bg: '#fef2f2', text: '#b91c1c', border: '#fee2e2' }
        };
        return configs[type] || { bg: '#f8fafc', text: '#475569', border: '#e2e8f0' };
    };

    const getFileUrl = (filePath) => {
        if (!filePath) return null;
        const baseUrl = process.env.REACT_APP_BASE_URL;
        const filename = filePath.split(/[/\\]/).pop();
        const encodedFilename = encodeURIComponent(filename);
        return `${baseUrl}/uploads/exam-routines/${encodedFilename}`;
    };

    const handleDownload = (routine) => {
        const fullUrl = getFileUrl(routine.filePath);
        if (fullUrl) {
            const a = document.createElement('a');
            a.href = fullUrl;
            a.download = routine.title || routine.name || 'exam-routine.pdf';
            a.target = '_blank';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        }
    };

    const routinesByClass = examRoutines.reduce((acc, routine) => {
        const className = routine.class?.sclassName || 'Unknown';
        if (!acc[className]) acc[className] = [];
        acc[className].push(routine);
        return acc;
    }, {});

    return (
        <PageWrapper>
            <PremiumHeader>
                <Container maxWidth="lg">
                    <Fade in timeout={800}>
                        <Box sx={{ position: 'relative', zIndex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)', width: 64, height: 64 }}>
                                    <DescriptionIcon sx={{ fontSize: 32 }} />
                                </Avatar>
                                <Box>
                                    <Typography variant="h3" sx={{ fontWeight: 900, letterSpacing: '-0.03em' }}>
                                        Exam Routines
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Fade>
                </Container>
            </PremiumHeader>

            <ContentContainer maxWidth="lg" sx={{ mt: 2 }}>
                {message.text && (
                    <Alert severity={message.type} sx={{ mb: 4, borderRadius: '16px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }} onClose={() => setMessage({ type: '', text: '' })}>
                        {message.text}
                    </Alert>
                )}

                {loading ? (
                    <Grid container spacing={3}>
                        {[1, 2, 3].map((i) => (
                            <Grid item xs={12} md={4} key={i}>
                                <Paper sx={{ p: 4, borderRadius: '24px', height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Typography color="textSecondary">Loading...</Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                ) : examRoutines.length === 0 ? (
                    <Zoom in>
                        <Paper sx={{ p: 10, textAlign: 'center', borderRadius: '32px', bgcolor: 'rgba(255,255,255,0.8)', backdropFilter: 'blur(10px)', border: '1px dashed #cbd5e1' }}>
                            <Box sx={{ animation: `${float} 3s ease-in-out infinite` }}>
                                <Avatar sx={{ width: 100, height: 100, bgcolor: '#f1f5f9', color: '#94a3b8', mx: 'auto', mb: 4 }}>
                                    <CalendarTodayIcon sx={{ fontSize: 48 }} />
                                </Avatar>
                            </Box>
                            <Typography variant="h4" sx={{ color: '#1e293b', fontWeight: 900, mb: 2 }}>
                                No Routines Yet
                            </Typography>
                            <Typography variant="h6" sx={{ color: '#64748b', fontWeight: 400, maxWidth: '500px', mx: 'auto' }}>
                                Examination schedules uploaded by the admin for your assigned classes will be displayed here.
                            </Typography>
                        </Paper>
                    </Zoom>
                ) : (
                    Object.entries(routinesByClass).map(([className, routines]) => (
                        <ClassSection key={className}>
                            <SectionLabel>
                                <Avatar sx={{ bgcolor: '#1e293b', width: 32, height: 32, fontSize: '0.875rem' }}>
                                    <ClassIcon sx={{ fontSize: 18 }} />
                                </Avatar>
                                <Typography variant="h5" sx={{ fontWeight: 800, color: '#1e293b' }}>
                                    Class {className}
                                </Typography>
                                <div className="line" />
                                <Chip label={`${routines.length} Schedules`} size="small" variant="outlined" sx={{ fontWeight: 700, color: 'white', borderColor: '#e2e8f0' }} />
                            </SectionLabel>

                            <Grid container spacing={4}>
                                {routines.map((routine, index) => {
                                    const typeStyles = getExamTypeColors(routine.examType);
                                    return (
                                        <Grid item xs={12} sm={6} md={4} key={routine._id}>
                                            <Fade in timeout={500 + (index * 100)}>
                                                <RoutineCard elevation={0}>
                                                    <TypeChip 
                                                        label={getExamTypeLabel(routine.examType)} 
                                                        sx={{ 
                                                            bgcolor: typeStyles.bg, 
                                                            color: typeStyles.text,
                                                            border: `1px solid ${typeStyles.border}`
                                                        }} 
                                                    />
                                                    
                                                    <IconBox className="icon-box">
                                                        <DescriptionIcon />
                                                    </IconBox>

                                                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b', mb: 2, lineHeight: 1.3, minHeight: '3.4em' }}>
                                                        {routine.title || routine.name}
                                                    </Typography>

                                                    <Box sx={{ mt: 'auto' }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <CalendarTodayIcon sx={{ fontSize: 16, color: '#64748b' }} />
                                                                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                                                                    EXAM DATE
                                                                </Typography>
                                                            </Box>
                                                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#334155' }}>
                                                                {formatDate(routine.examDate)}
                                                            </Typography>
                                                        </Box>

                                                        <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <Tooltip title="Date when this routine was uploaded">
                                                                    <InfoOutlinedIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
                                                                </Tooltip>
                                                                <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600 }}>
                                                                    UPLOADED {formatDate(routine.createdAt)}
                                                                </Typography>
                                                            </Box>
                                                        </Box>

                                                        <Button
                                                            fullWidth
                                                            variant="contained"
                                                            className="download-btn"
                                                            startIcon={<DownloadIcon />}
                                                            onClick={() => handleDownload(routine)}
                                                            sx={{ 
                                                                borderRadius: '16px', 
                                                                py: 1.5, 
                                                                textTransform: 'none', 
                                                                fontWeight: 800,
                                                                bgcolor: '#f1f5f9',
                                                                color: '#475569',
                                                                boxShadow: 'none',
                                                                '&:hover': {
                                                                    bgcolor: '#6366f1',
                                                                    color: 'white',
                                                                    boxShadow: '0 8px 16px -4px rgba(99, 102, 241, 0.3)'
                                                                }
                                                            }}
                                                        >
                                                            Download PDF
                                                        </Button>
                                                    </Box>
                                                </RoutineCard>
                                            </Fade>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </ClassSection>
                    ))
                )}
            </ContentContainer>
        </PageWrapper>
    );
};

export default TeacherExamRoutine;
